import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  LinearProgress,
  Box,
  TableBody,
  TableCell,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryColorTableCell from '../../../components/PrimaryColorTableCell/PrimaryColorTableCell';
import Histogram from '../../../model/API/Histogram/Histogram';
import HistogramTableRow from '../model/HistogramTableRow';

const TimeTable: React.FC<TimeTableProps> = ({ time, loading, error, unit }: TimeTableProps) => {
  const { t } = useTranslation();

  const tableItems = useMemo<HistogramTableRow[]>(() => {
    if (!time) {
      return [];
    }

    return Array.from({ length: time.nbins - 1 }, (_: unknown, index: number) => {
      const value = time.values[index];
      const rangeLowerBound = time.edges[index];
      const rangeUpperBound = time.edges[index + 1];
      const percent = time.total > 0 ? +((value / time.total) * 100).toFixed(2) : 0;

      return {
        range: `${rangeLowerBound} - ${rangeUpperBound}`,
        value: +value.toFixed(2),
        percent,
      };
    });
  }, [time]);

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <PrimaryColorTableCell align="center">
              {t('statistics_header_hours')}
            </PrimaryColorTableCell>
            <PrimaryColorTableCell align="center">{unit}</PrimaryColorTableCell>
            <PrimaryColorTableCell align="center">{t('percent')}</PrimaryColorTableCell>
          </TableRow>
        </TableHead>
        {!loading && (
          <TableBody>
            {tableItems.length > 0 ? (
              tableItems.map((item: HistogramTableRow) => (
                <TableRow hover key={item.range}>
                  <TableCell align="center">
                    <Box fontWeight="fontWeightMedium">{item.range}</Box>
                  </TableCell>
                  <TableCell align="center">{`${item.value}${unit ? ` ${unit}` : ''}`}</TableCell>
                  <TableCell align="center">{item.percent} %</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow hover>
                <TableCell colSpan={3} align="center">
                  <Typography variant="subtitle1" color="gray" paddingTop={8} paddingBottom={8}>
                    {error || t('no_data_found')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
      {loading && <LinearProgress color="primary" />}
    </TableContainer>
  );
};

interface TimeTableProps {
  time: Histogram | null;
  loading: boolean;
  error: string | null;
  unit: string;
}

export default TimeTable;
